import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fmt2, fmtPct } from '../../components/ui/shared';
import usePageTitle from '../../hooks/usePageTitle';

const gradeColor = g => {
  if(!g) return "var(--text-muted)";
  if(g.startsWith("A")) return "var(--success)";
  if(g.startsWith("B")) return "var(--accent)";
  if(g.startsWith("C")) return "var(--warning)";
  return "var(--danger)";
};

const typeBadge = t => {
  if(t==="signal_sync")  return {label:"   SignalSync™", color:"var(--accent)",   bg:"rgba(78,154,241,0.12)"};
  if(t==="live_trader")  return {label:"   Live Trader", color:"var(--success)",  bg:"rgba(34,197,94,0.1)"};
  return                        {label:"   Analyst",     color:"var(--text-muted)",bg:"var(--bg-elevated)"};
};

export default function LeaderboardPage() {
  usePageTitle("Leaderboard");
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy]   = useState("alphaScore");

  useEffect(()=>{
    Promise.all([
      fetch("/api/public/marketplace").then(r=>r.json()),
      fetch("/api/public/traders?limit=100&sort=alpha_score").then(r=>r.json()),
    ]).then(([sigRes, traderRes])=>{
      const signals = (sigRes.sets||[]).map(s=>({
        id: "sig_"+s.id,
        _setId: s.id,
        _traderId: null,
        name: s.name,
        subName: (s.riskLevel||"MEDIUM")+" Risk · by "+s.analystName,
        sourceType: s.sourceType||"manual_analyst",
        grade: s.virtualAlphaGrade||s.alphaGrade||"D",
        alphaScore: s.virtualAlphaScore||s.alphaScore||0,
        roi: s.virtualRoiPct||0,
        maxDD: s.virtualMaxDDPct||0,
        aum: s.totalAUM||0,
        followers: s.subscribers||0,
        winRate: s.winRate||0,
        profitFactor: s.profitFactor||0,
        riskLevel: s.riskLevel || (()=>{ const sc=s.virtualAlphaScore||s.alphaScore||0; return sc<30?"EXTREME":sc<50?"HIGH":sc<70?"MEDIUM":sc>=85?"VERIFIED_SAFE":"LOW"; })(),
      }));
      const traders = (traderRes.traders||[]).map(t=>({
        id: "tr_"+(t.id||t.traderId),
        _setId: null,
        _traderId: t.id||t.traderId,
        name: t.nickname||t.traderName||t.trader_name||"Trader",
        subName: (t.riskLevel||"MEDIUM")+" Risk · "+(t.platform||""),
        sourceType: t.signalSyncStatus==="ACTIVE"?"signal_sync":"live_trader",
        grade: t.grade||"D",
        alphaScore: t.alphaScore||t.alpha_score||0,
        roi: t.roi||0,
        maxDD: t.maxDD||t.max_drawdown_pct||0,
        aum: t.totalAUM||t.aum||0,
        followers: t.totalFollowers||t.copyFollowers||0,
        description: t.broker||'',
        accountAge: t.accountAge||'',
        winRate: t.winRate||t.win_rate||0,
        profitFactor: t.profitFactor||t.profit_factor||0,
      }));
      setRows([...signals,...traders]);
    }).finally(()=>setLoading(false));
  },[]);

  const sorted = [...rows].sort((a,b)=>{
    if(sortBy==="roi") return b.roi-a.roi;
    if(sortBy==="maxDD") return a.maxDD-b.maxDD;
    if(sortBy==="followers") return b.followers-a.followers;
    if(sortBy==="aum") return b.aum-a.aum;
    return b.alphaScore-a.alphaScore;
  });

  const S = {
    th: {padding:"10px 12px",textAlign:"left",fontSize:11,fontWeight:700,color:"var(--text-faint)",
         borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap",textTransform:"uppercase",letterSpacing:"0.05em"},
    td: {padding:"12px 12px",fontSize:13,color:"var(--text-muted)",borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap"},
  };

  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",
    borderRadius:"var(--radius-md)",padding:"6px 12px",fontSize:13};

  return (
    <div style={{padding:"24px 0",maxWidth:1200,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:"var(--text-main)",margin:0}}>Leaderboard</h1>
          <p style={{color:"var(--text-muted)",fontSize:13,margin:"4px 0 0"}}>All strategies ranked by AlphaScore™ — traders, analysts & SignalSync</p>
        </div>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={selStyle}>
          <option value="alphaScore">Sort: AlphaScore</option>
          <option value="roi">Sort: ROI</option>
          <option value="maxDD">Sort: Lowest DD</option>
          <option value="aum">Sort: AUM Managed</option>
        </select>
      </div>

      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",overflow:"hidden"}}>
        {loading ? (
          <div style={{padding:60,textAlign:"center",color:"var(--text-faint)"}}>Loading...</div>
        ) : sorted.length===0 ? (
          <div style={{padding:60,textAlign:"center",color:"var(--text-faint)"}}>No strategies yet</div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
              <thead>
                <tr style={{background:"var(--bg-elevated)"}}>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Strategy</th>
                  <th style={S.th}>Grade</th>
                  <th style={S.th}>AlphaScore</th>
                  <th style={S.th}>ROI</th>
                  <th style={S.th}>Max DD</th>
                  <th style={S.th}>Broker / Age</th>
                  <th style={S.th}>AUM</th>
                  <th style={S.th}>Followers</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r,i)=>{
                  const badge = typeBadge(r.sourceType);
                  const gc = gradeColor(r.grade);
                  const roiColor = r.roi>=0?"var(--success)":"var(--danger)";
                  const ddColor = r.maxDD>20?"var(--danger)":r.maxDD>10?"var(--warning)":"var(--success)";
                  return (
                    <tr key={r.id} style={{background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                      <td style={S.td}>
                        <span style={{fontWeight:700,color:i<3?"var(--accent)":"var(--text-faint)"}}>{i+1}</span>
                      </td>
                      <td style={S.td}>
                        <div style={{fontWeight:700,color:"var(--text-main)",marginBottom:3}}>{r.name}</div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:10,padding:"1px 6px",borderRadius:4,background:badge.bg,color:badge.color,fontWeight:700}}>{badge.label}</span>
                          <span style={{fontSize:11,color:"var(--text-faint)"}}>{r.subName}</span>
                        </div>
                      </td>
                      <td style={S.td}>
                        <span style={{fontWeight:800,color:gc,fontSize:15}}>{r.grade}</span>
                      </td>
                      <td style={S.td}>
                        <span style={{fontWeight:700,color:gc}}>{fmt2(r.alphaScore)}</span>
                      </td>
                      <td style={S.td}>
                        <span style={{fontWeight:700,color:roiColor}}>{r.roi>=0?"+":""}{fmt2(r.roi)}%</span>
                      </td>
                      <td style={S.td}>
                        <span style={{fontWeight:700,color:ddColor}}>{fmt2(r.maxDD)}%</span>
                      </td>
                      <td style={{...S.td,fontSize:11,color:"var(--text-muted)"}}>{r.description||'—'} · {r.accountAge||'—'}</td>
                      <td style={S.td}>{r.aum>0?("$"+(r.aum>=1000000?(r.aum/1000000).toFixed(1)+"M":r.aum>=1000?(r.aum/1000).toFixed(1)+"K":r.aum.toFixed(0))):"—"}</td>
                      <td style={S.td}>{r.followers||0}</td>
                      <td style={S.td}>
                        <button onClick={()=>{
                          if(r._setId) navigate(`/marketplace/analyst/${r._setId}`);
                          else if(r._traderId) navigate(`/trader/${r._traderId}`);
                        }} style={{padding:"5px 12px",borderRadius:6,border:"1px solid var(--accent-border)",
                          background:"var(--accent-soft)",color:"var(--accent)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
